import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Heading,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { RiArrowLeftFill } from "react-icons/ri";
import { AssetTransactionsCard } from "~/components/Asset/AssetTransactionsCard";
import { AssetBalanceChart } from "~/components/Asset/BalanceChart";
import { ExternalBankAccountAssetDetailsCard } from "~/components/Asset/ExternalBankAccountAssetDetails";
import { FixedIncomeFundAssetDetailsCard } from "~/components/Asset/FixedIncomeFundAssetDetails";
import { FXAssetDetailsCard } from "~/components/Asset/FXAssetDetails";
import { NoticeAccountAssetDetailsCard } from "~/components/Asset/NoticeAccountAssetDetails";
import { PaymentIdentifiersCard } from "~/components/Asset/PaymentIdentifiers";
import { SavingsAssetDetailsCard } from "~/components/Asset/SavingsAssetDetails";
import { TermDepositAssetDetailsCard } from "~/components/Asset/TermDepositAssetDetails";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AssetLedgerAccountCard } from "~/components/Ledger/LedgerAccountCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminAssetDetailQuery } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/assets_/$assetId")({
  head: () => opsHead("Asset"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <AssetDetailPage />
    </Suspense>
  ),
});

const AssetDetailPage = () => {
  const params = useParams({ strict: false });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminAssetDetail($id: ID!) {
        asset(id: $id) {
          id
          class
          currency
          feeRate
          name
          openedAt
          status
          settlementAccount {
            ... on FXAsset {
              ...AssetCardFields
            }
            ... on SavingsAsset {
              ...AssetCardFields
            }
            ... on BeneficiaryAccount {
              ...BeneficiaryAssetCardFields
            }
          }
          estimatedValue {
            amount
            currency
          }
          estimatedValueChangeOverMonth {
            amount
            currency
          }
          estimatedValueWithOpenFundMovements {
            amount
            currency
          }

          assetRule {
            id
            evaluationStatus
            evaluationCadence
            nextEvaluationDueAt
            ...on AlertAssetRule {
              minimumBalanceThreshold {
                amount
                currency
              }
              maximumBalanceThreshold {
                amount
                currency
              }
            }
            ...on TopUpAssetRule {
              balanceThreshold {
                currency
                amount
              }
              balanceTarget {
                currency
                amount
              }
            }
          }

          assetLedgerAccounts {
            id
            ... AssetLedgerAccountCardFields
          }

          businessEntity {
            id
            eburyClients {
              id
            }
            currencycloudAccounts {
              id
            }
            organisation {
              id
              ... OrganisationCardFields
              assets {
                  id
                  name
                  businessEntity {
                      id
                      name
                  }
              }
            }
            ... BusinessEntityCardFields
          }

          custodyAccount {
            id
            shortRef
          }

          ... @defer {
            eburyBeneficiary {
              id
              ...AdminEburyBeneficiaryCard
            }
          }

          ... @defer {
            currencycloudBeneficiary {
              id
              ...AdminCurrencycloudBeneficiaryCard
            }
          }

          paymentIdentifiers {
            id
            ... PaymentIdentifierComponent
          }

          ... on FixedIncomeFundAsset {
            product {
              id

              provider {
                ... ProviderCardFields
              }

              ... ProductCardFields
            }
          }

          ... on FXAsset {
            eburyAccount {
              id
              active
              clientId
            }
            currencycloudBalance {
              id
              account {
                id
              }
            }
            currencycloudAccount {
              id
            }
            provider {
              id
              name
              internalName
            }
          }

          ... on ExternalBankAccountAsset {
            id
            details {
              id
            }
          }

          ... on NoticeAccountAsset {
            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }

          ... on SavingsAsset {
            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }

          ... on TermDepositAsset {
            maturityInstructions {
              ...on TermDepositMaturityInstructionsRolloverPrinciple {
                  toAsset {
                      id
                      ... AssetCardFields
                  }
              }
              ...on TermDepositMaturityInstructionsTransfer {
                  toAsset {
                      id
                      ... AssetCardFields
                  }
              }
            }

            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }
        }
      }
      `),
    { variables: { id: params.assetId ?? "" } },
  );

  const [deleteAsset, { loading: deleteAssetLoading }] = useMutation(
    graphql(`
     mutation DeleteAsset($input: DeleteAssetInput!) {
       deleteAsset(input: $input) {
         organisation {
           id
         }
       }
     }
    `),
    {
      variables: {
        input: {
          assetId: params.assetId ?? "",
        },
      },
      onCompleted: (data) => {
        toast({
          kind: "success",
          title: "Asset deleted",
        });
        navigate({
          to: "/organisations/$organisationId",
          params: { organisationId: data.deleteAsset.organisation.id },
        });
      },
      onError: (err) => {
        toast({
          kind: "error",
          title: "Something went wrong deleting asset",
          description: err.message,
        });
        setConfirmDialogOpen(false);
      },
    },
  );

  const asset = data?.asset;
  if (!asset) {
    throw new NotFoundError();
  }

  useOpsTitle(`${asset.businessEntity?.name ?? ""} • ${asset.name}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`${asset.businessEntity?.name ?? ""} • ${asset.name}`}
        subtitle={asset.id}
      />
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-4">
          <AssetDetailsCard asset={asset} className="flex-grow" />

          <PaymentIdentifiersCard asset={asset} />
        </div>

        <AssetCurrentBalanceDetails asset={asset} />

        <AssetBalanceChart assetId={asset.id} />

        <AssetTransactionsCard
          assetId={asset.id}
          orgId={asset.businessEntity?.organisation?.id ?? ""}
          assetName={asset.name}
          assetClass={asset.class}
        />

        <div className="flex flex-row gap-4">
          {(asset.businessEntity?.eburyClients ?? []).length > 0 &&
            asset.class !== "FX" && (
              <Card className="p-2">
                <CardHeader>
                  <CardTitle>
                    <Heading size="2xl" as="h2">
                      Ebury
                    </Heading>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <Txt as="p">
                    Sync this asset to allow it to be used as a beneficiary of
                    Ebury transactions
                  </Txt>

                  <AdminEburyBeneficiaryCard
                    eburyBeneficiary={asset.eburyBeneficiary}
                    assetId={asset.id}
                    errors={error?.graphQLErrors?.filter((gqlError) => {
                      return (
                        gqlError.path?.at(0) === "asset" &&
                        gqlError.path?.at(1) === "eburyBeneficiary"
                      );
                    })}
                  />
                </CardContent>
              </Card>
            )}

          {(asset.businessEntity?.currencycloudAccounts ?? []).length > 0 &&
            asset.class !== "FX" && (
              <Card className="p-2">
                <CardHeader>
                  <CardTitle>
                    <Heading size="2xl" as="h2">
                      Visa
                    </Heading>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <Txt as="p">
                    Sync this asset to allow it to be used as a beneficiary of
                    Visa transactions
                  </Txt>

                  <AdminCurrencycloudBeneficiaryCard
                    currencycloudBeneficiary={asset.currencycloudBeneficiary}
                    assetId={asset.id}
                    errors={error?.graphQLErrors?.filter((gqlError) => {
                      return (
                        gqlError.path?.at(0) === "asset" &&
                        gqlError.path?.at(1) === "currencycloudBeneficiary"
                      );
                    })}
                  />
                </CardContent>
              </Card>
            )}

          <Card className="flex-grow p-2">
            <CardHeader>
              <CardTitle>
                <Heading size="2xl" as="h2">
                  Ledger Accounts
                </Heading>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {asset.assetLedgerAccounts?.map((assetLedgerAccount) => (
                <AssetLedgerAccountCard
                  {...assetLedgerAccount}
                  key={assetLedgerAccount.id}
                />
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-row justify-end">
          {asset.class !== "EXTERNAL_BANK_ACCOUNT" && (
            <Dialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => setConfirmDialogOpen(true)}
                  variant="destructive"
                >
                  Delete asset
                </Button>
              </DialogTrigger>

              <DialogContent hideCloseButton>
                <DialogHeader>
                  <DialogTitle>Permenantly delete {asset.name}</DialogTitle>
                  <DialogDescription>
                    <Txt weight="semibold">ATTENTION</Txt>
                    <br /> This will permenantly delete this asset from the
                    database. It can not be recovered after this action has been
                    performed.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    className="px-6"
                    size="lg"
                    variant="secondary"
                    onClick={(e) => {
                      setConfirmDialogOpen(false);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <RiArrowLeftFill className="mr-2" /> Back
                  </Button>

                  <div className="flex flex-row items-center gap-x-4">
                    <Button
                      variant="destructive"
                      className="px-6"
                      size="lg"
                      loading={deleteAssetLoading}
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteAsset();
                      }}
                    >
                      Confirm deletion
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
};

function AssetCurrentBalanceDetails({
  asset,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-6">
        <div>
          <div className="text-secondary">Current balance</div>
          <CurrencyAmountUI
            amount={asset.estimatedValue?.amount}
            currency={asset.estimatedValue?.currency}
            size="2xl"
          />
        </div>

        <div>
          <div className="text-secondary">
            Balance (including open fund movements)
          </div>
          <CurrencyAmountUI
            amount={asset.estimatedValueWithOpenFundMovements?.amount}
            currency={asset.estimatedValueWithOpenFundMovements?.currency}
            size="2xl"
          />
        </div>

        <div>
          <div className="text-secondary">Change 1 month</div>
          <CurrencyAmountUI
            amount={asset.estimatedValueChangeOverMonth?.amount}
            currency={asset.estimatedValueChangeOverMonth?.currency}
            size="2xl"
            showSign
          />
        </div>
      </CardContent>
    </Card>
  );
}

function AssetDetailsCard({
  asset,
  className,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
  className?: string;
}) {
  switch (asset.__typename) {
    case "ExternalBankAccountAsset":
      return (
        <ExternalBankAccountAssetDetailsCard
          asset={asset}
          className={className}
        />
      );
    case "FXAsset":
      return <FXAssetDetailsCard asset={asset} className={className} />;
    case "FixedIncomeFundAsset":
      return (
        <FixedIncomeFundAssetDetailsCard asset={asset} className={className} />
      );
    case "NoticeAccountAsset":
      return (
        <NoticeAccountAssetDetailsCard asset={asset} className={className} />
      );
    case "SavingsAsset":
      return <SavingsAssetDetailsCard asset={asset} className={className} />;
    case "TermDepositAsset":
      return (
        <TermDepositAssetDetailsCard asset={asset} className={className} />
      );
  }
}
